import { useNavigate } from "react-router";
import {
  dashboard,
  audit,
  niches,
  type DashboardData,
  type AuditEvent,
  type NicheSummary,
} from "../../api/client";
import { useDuviCoreAdminQuery } from "../../hooks/use-api";

const FALLBACK: DashboardData = {
  open_platform_tickets: 0,
  audit_events_last_24h: 0,
  total_users: 0,
  generated_at: "",
};

export interface DuviCoreAdminKpi {
  icon: string;
  label: string;
  testId: string;
  value: string;
  footnote: string;
  error: boolean;
  go: string;
}

export function useDuviCoreAdminDashboard() {
  const navigate = useNavigate();
  const { data, isLoading } = useDuviCoreAdminQuery(() => dashboard.get());
  const { data: recentAuditData } = useDuviCoreAdminQuery(() =>
    audit.list({ limit: 5 }),
  );
  const { data: nicheHealthData } = useDuviCoreAdminQuery(() =>
    niches.list({ limit: 5 }),
  );

  const d: DashboardData = data ?? FALLBACK;
  const recentAudit: AuditEvent[] = recentAuditData ?? [];
  const nicheHealth: NicheSummary[] = nicheHealthData ?? [];

  const kpis: DuviCoreAdminKpi[] = [
    {
      icon: "message",
      label: "Open platform tickets",
      // Stable hook for the E2E "real function" assertion (epic 012 / story 15)
      // — lets the smoke test read the resolved KPI value deterministically.
      testId: "kpi-open-platform-tickets",
      value: isLoading ? "—" : String(d.open_platform_tickets),
      footnote:
        d.open_platform_tickets > 0 ? "Awaiting triage" : "All caught up",
      error: d.open_platform_tickets > 0,
      go: "/support/tickets",
    },
    {
      icon: "activity",
      label: "Audit events (last 24h)",
      testId: "kpi-audit-events-24h",
      value: isLoading ? "—" : String(d.audit_events_last_24h),
      footnote: "In the last 24 hours",
      error: false,
      go: "/audit-log",
    },
    {
      icon: "users",
      label: "Total users",
      testId: "kpi-total-users",
      value: isLoading ? "—" : String(d.total_users),
      footnote: "Platform admin accounts",
      error: false,
      go: "/user-management",
    },
  ];

  return {
    kpis,
    recentAudit,
    nicheHealth,
    generatedAt: d.generated_at,
    navigate,
  };
}
